import { Inject } from '@angular/core';
import { NgZone } from '@angular/core';
import { AfterViewInit } from '@angular/core';
import { OnDestroy } from '@angular/core';
import { OnInit } from '@angular/core';
import { Input } from '@angular/core';
import { Directive } from '@angular/core';
import { ElementRef } from '@angular/core';
import { ResizedDirective } from 'angular-resize-event';

@Directive({ selector: '[overflowMarker]' })
export class OverflowMarker implements OnInit, AfterViewInit, OnDestroy {

  private static readonly MARKER_CLASS_SUFFIXES = {
    top: '-top',
    right: '-right',
    bottom: '-bottom',
    left: '-left',
  };

  @Input() readonly overflowSelectors: string;
  @Input() readonly markPrefix: string = 'over';
  @Input() readonly overByAtLeastPx: number = 1;

  private resizedDirective: ResizedDirective;

  constructor(
    private readonly container: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
  ) {
  }

  ngOnInit(): void {

    this.resizedDirective = new ResizedDirective(this.container, this.ngZone);

    this.resizedDirective.resized.subscribe(() => this.mark());

    this.resizedDirective.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.mark();
  }

  ngOnDestroy(): void {

    this.resizedDirective.resized.unsubscribe();

    this.resizedDirective.ngOnDestroy();
  }

  private mark(): void {

    const descendants =
      this.container.nativeElement
        .querySelectorAll<HTMLElement>(this.overflowSelectors);

    Array.from(descendants)
      .forEach(descendant => {

        this.isShowing(descendant)
          ? this.markShowingDescendant(descendant)
          : this.markHiddenDescendant(descendant);
      });
  }

  private isShowing(
    element: HTMLElement
  ): boolean {
    return !!element.offsetHeight && !!element.offsetWidth;
  }

  private markHiddenDescendant(
    descendant: HTMLElement,
  ) {

    const originalDisplay = descendant.style.display;
    const originalVisibility = descendant.style.visibility;

    try {

      descendant.style.display = 'block';
      descendant.style.visibility = 'hidden';

      this.markShowingDescendant(descendant);

    } finally {

      descendant.style.display = originalDisplay;
      descendant.style.visibility = originalVisibility;
    }
  }

  private markShowingDescendant(
    descendant: HTMLElement
  ): void {

    const containerBounds = this.container.nativeElement.getBoundingClientRect();
    const descendantBounds = descendant.getBoundingClientRect();

    const overflowingTop = this.isOverflowingTop(containerBounds, descendantBounds);
    const overflowingRight = this.isOverflowingRight(containerBounds, descendantBounds);
    const overflowingBottom = this.isOverflowingBottom(containerBounds, descendantBounds);
    const overflowingLeft = this.isOverflowingLeft(containerBounds, descendantBounds);

    const topMarker = this.markPrefix + OverflowMarker.MARKER_CLASS_SUFFIXES.top;
    const rightMarker = this.markPrefix + OverflowMarker.MARKER_CLASS_SUFFIXES.right;
    const bottomMarker = this.markPrefix + OverflowMarker.MARKER_CLASS_SUFFIXES.bottom;
    const leftMarker = this.markPrefix + OverflowMarker.MARKER_CLASS_SUFFIXES.left;

    descendant.classList.toggle(topMarker, overflowingTop);
    descendant.classList.toggle(rightMarker, overflowingRight);
    descendant.classList.toggle(bottomMarker, overflowingBottom);
    descendant.classList.toggle(leftMarker, overflowingLeft);
  }

  private isOverflowingTop(
    containerBounds: DOMRect,
    elementBounds: DOMRect
  ): boolean {

    const overflowAmountPx = (containerBounds.top - elementBounds.top);

    return overflowAmountPx >= this.overByAtLeastPx;
  }

  private isOverflowingRight(
    containerBounds: DOMRect,
    elementBounds: DOMRect
  ): boolean {

    const overflowAmountPx = (elementBounds.right - containerBounds.right);

    return overflowAmountPx >= this.overByAtLeastPx;
  }

  private isOverflowingBottom(
    containerBounds: DOMRect,
    elementBounds: DOMRect
  ): boolean {

    const overflowAmountPx = (elementBounds.bottom - containerBounds.bottom);

    return overflowAmountPx >= this.overByAtLeastPx;
  }

  private isOverflowingLeft(
    containerBounds: DOMRect,
    elementBounds: DOMRect
  ): boolean {

    const overflowAmountPx = (containerBounds.left - elementBounds.left);

    return overflowAmountPx >= this.overByAtLeastPx;
  }

}
